import React, { useCallback, useEffect, useRef } from "react";
import styles from "./NotificationTemplates.module.css";
import type { EmailTemplateView } from "../../../services/buyAdminApi";

const TRUSTED_HTML_VARIABLES = ["inHandExpectedAvailabilityBlock"];

type TemplateTileProps = {
  title: string;
  subtitle?: string;
  meta?: string;
  ready?: boolean;
  selected?: boolean;
  onClick: () => void;
};

export const TemplateTile = ({
  title,
  subtitle,
  meta,
  ready = true,
  selected = false,
  onClick,
}: TemplateTileProps) => (
  <button
    type="button"
    className={`${styles.tile} ${selected ? styles.tileSelected : ""}`}
    onClick={onClick}
  >
    <div className={styles.tileTitle}>{title}</div>
    {subtitle ? <div className={styles.tileSubtitle}>{subtitle}</div> : null}
    <div className={styles.tileMeta}>
      <span className={ready ? styles.readyBadge : styles.pendingBadge}>
        {ready ? "Ready" : "Not uploaded"}
      </span>
      {meta ? <span>{meta}</span> : null}
    </div>
  </button>
);

export const renderTemplatePreview = (
  template: string,
  variables: Record<string, string>,
  options?: { escapeValues?: boolean; rawKeys?: string[] },
): string => {
  const escapeValues = options?.escapeValues ?? true;
  const rawKeys = new Set(options?.rawKeys || []);
  const escapeHtml = (value: string): string =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key: string) => {
    const raw = variables[key] ?? "";
    return escapeValues && !rawKeys.has(key) ? escapeHtml(raw) : raw;
  });
};

export type EmailHtmlDraft = {
  folder: string;
  view: EmailTemplateView;
  file: File;
  raw: string;
};

export type EmailMascotDraft = {
  folder: string;
  file: File;
  previewUrl: string;
};

export const buildEmailDraftPreview = (params: {
  draft: EmailHtmlDraft | null;
  folder: string;
  view: EmailTemplateView;
  savedHtml: string | null | undefined;
  htmlTemplate?: string | null;
  subjectTemplate?: string | null;
  savedSubject?: string | null;
  draftSubject?: string | null;
  sampleVariables: Record<string, string>;
  mascotUrl?: string | null;
  mascotDraft?: EmailMascotDraft | null;
  footerLogoUrl?: string | null;
}): { html: string; subject: string; hasDraft: boolean } => {
  const hasHtmlDraft =
    Boolean(params.draft) &&
    params.draft!.folder === params.folder &&
    params.draft!.view === params.view;

  const variables = {
    ...params.sampleVariables,
    logoUrl:
      params.footerLogoUrl ||
      params.sampleVariables.logoUrl ||
      "",
    mascotUrl:
      (params.mascotDraft?.folder === params.folder ? params.mascotDraft.previewUrl : null) ||
      params.mascotUrl ||
      params.sampleVariables.mascotUrl ||
      "",
  };

  const subjectTemplate =
    params.draftSubject != null ? params.draftSubject : params.subjectTemplate;

  if (hasHtmlDraft && params.draft) {
    const html = renderTemplatePreview(params.draft.raw, variables, {
      rawKeys: TRUSTED_HTML_VARIABLES,
    });
    const subject = subjectTemplate
      ? renderTemplatePreview(subjectTemplate, variables, { escapeValues: false })
      : params.savedSubject || "";
    return { html, subject, hasDraft: true };
  }

  const subject = subjectTemplate
    ? renderTemplatePreview(subjectTemplate, variables, { escapeValues: false })
    : params.savedSubject || "";

  if (params.htmlTemplate) {
    return {
      html: renderTemplatePreview(params.htmlTemplate, variables, {
        rawKeys: TRUSTED_HTML_VARIABLES,
      }),
      subject,
      hasDraft: params.draftSubject != null,
    };
  }

  return {
    html: params.savedHtml || "",
    subject,
    hasDraft: params.draftSubject != null,
  };
};

type EmailClientPreviewProps = {
  html: string;
  subject?: string;
};

const AutoHeightEmailFrame = ({
  html,
  title,
  className,
}: {
  html: string;
  title: string;
  className: string;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resizeFrame = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc?.body) return;
    const height = Math.max(
      doc.body.scrollHeight,
      doc.documentElement.scrollHeight,
      120,
    );
    iframe.style.height = `${height}px`;
  }, []);

  useEffect(() => {
    resizeFrame();
  }, [html, resizeFrame]);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      className={className}
      srcDoc={html}
      onLoad={resizeFrame}
      scrolling="no"
    />
  );
};

export const EmailClientPreview = ({ html, subject }: EmailClientPreviewProps) => {
  const displaySubject = subject?.trim() || "QuickAsyst order update";

  return (
    <div className={styles.emailPreviewStage}>
      <div className={styles.emailClientShell}>
        <div className={styles.emailClientToolbar}>
          <span className={styles.emailClientToolbarTitle}>Inbox — QuickAsyst</span>
          <span className={styles.emailClientToolbarMeta}>Preview mode</span>
        </div>
        <div className={styles.emailClientMessage}>
          <div className={styles.emailClientHeader}>
            <div className={styles.emailClientAvatar}>Q</div>
            <div className={styles.emailClientHeaderText}>
              <div className={styles.emailClientFromRow}>
                <span className={styles.emailClientFrom}>QuickAsyst</span>
                <span className={styles.emailClientAddress}>&lt;orders@quickasyst.com&gt;</span>
                <span className={styles.emailClientDate}>Just now</span>
              </div>
              <div className={styles.emailClientSubject}>{displaySubject}</div>
              <div className={styles.emailClientTo}>to me</div>
            </div>
          </div>
          <div className={styles.emailClientBody}>
            <AutoHeightEmailFrame
              html={html}
              title="Desktop email preview"
              className={styles.emailClientFrame}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
